// Project-attachment persistence — the tier below `projectApp.public`, for a
// workbook too large for its ~20,000-char cap.
//
// LIFTED, near-verbatim, from ~/Documents/code/canonicalGiraffe/src/sdk/
// attachmentStore.ts, where every call shape below was verified against a
// running Giraffe across several SDK builds. Do not "simplify" the multi-shape
// probing or the retry backoffs — each one exists because a single-shape
// version failed in production. The binary / model-uri paths were dropped;
// only the JSON round-trip gTables needs is kept.
//
// It imports `rpc` directly rather than going through giraffe/adapter.ts,
// because the probing needs both the array and bare-argument invoke forms.

import { giraffeState, rpc } from '@gi-nx/iframe-sdk';

/**
 * Project-attachment persistence — the FALLBACK path for state too big for
 * `projectApp.public` (which throws `too large` past an undocumented limit),
 * or for genuinely file-shaped/binary blobs (e.g. a GLB model).
 *
 * The SDK exposes only `uploadProjectAttachment` for writes and no
 * list/get/download at all. So:
 *   - Write: `rpc.invoke('uploadProjectAttachment', [{ buffer, contentType,
 *     name, attachmentId? }])`. Passing `attachmentId` upserts in place;
 *     omitting it creates a new row (and accumulates duplicates).
 *   - Read: enumerate via `fetchProjectDetails` → `{ attachments: [{ id,
 *     name, url, updated_at }] }`, pick by name, then fetch the blob. The
 *     listed `url` is relative and short-lived, so we resolve it against the
 *     PARENT origin and mint a fresh signed URL with `getTempUrl` per fetch.
 *
 * All JSON here rides the `application/json` upload path — the parent's API
 * rejects large raw-binary uploads with an HTML error page, so binary callers
 * (glbFlush) wrap their bytes in a base64 JSON envelope and hand us the
 * envelope object. See `glbFlush.ts`.
 */

// Session cache of name → attachmentId so repeat uploads upsert instead of
// creating duplicates. Reset on reload (that's fine — the recovery path in
// loadJsonAttachment re-discovers ids by name).
const attachmentIdByName = new Map<string, number>();

// Statuses that mean "the signed URL went stale / the blob isn't reachable
// yet" — worth a backoff + re-mint retry (upload races, expired temp urls).
const RETRYABLE_STATUS = [401, 403, 404, 410];

// ─────────────────────────────────────────────────────────────────────────
// Parent-origin + project-id resolution
// ─────────────────────────────────────────────────────────────────────────

/**
 * We run in a cross-origin iframe; attachment URLs come back relative
 * (`/api/storage-proxy/…`) and must be resolved against the PARENT origin,
 * not ours. `ancestorOrigins` is the reliable Chromium source; `referrer`
 * is the cross-browser fallback.
 */
function parentOrigin(): string {
  try {
    const anc = (window.location as unknown as { ancestorOrigins?: DOMStringList })
      .ancestorOrigins;
    if (anc && anc.length > 0) return anc[0] ?? '';
  } catch {
    /* ignore */
  }
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch {
    /* ignore */
  }
  return '';
}

/** The current project id, tried across the shapes different builds expose. */
function projectId(): string | number | null {
  const p = giraffeState.get('project') as Record<string, unknown> | null | undefined;
  if (!p) return null;
  const props = p['properties'] as Record<string, unknown> | undefined;
  const pid =
    p['id'] ??
    p['projectId'] ??
    p['project_id'] ??
    p['pk'] ??
    props?.['id'] ??
    props?.['project_id'];
  if (typeof pid === 'string' || typeof pid === 'number') return pid;
  return null;
}

function absoluteUrl(rawUrl: string): string {
  if (/^https?:/i.test(rawUrl)) return rawUrl;
  const origin = parentOrigin();
  if (!origin) return rawUrl;
  return `${origin}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ─────────────────────────────────────────────────────────────────────────
// Upload
// ─────────────────────────────────────────────────────────────────────────

type AttachmentRow = { id?: number; name?: string; url?: string; updated_at?: string };

/**
 * The upload response is shaped inconsistently across Giraffe builds —
 * sometimes the new id is top-level, sometimes nested under `data`. Probe the
 * usual spots; return null if none match (caller keeps the previous id).
 */
function pickAttachmentId(res: unknown): number | null {
  if (!res || typeof res !== 'object') return null;
  const o = res as Record<string, unknown>;
  const data = o['data'] as Record<string, unknown> | undefined;
  const candidates = [o['attachmentId'], o['id'], data?.['id'], data?.['attachmentId']];
  for (const c of candidates) {
    if (typeof c === 'number') return c;
  }
  return null;
}

/** Names this session has already created a row for. See `uploadJsonAttachment`. */
const created = new Set<string>();

/**
 * The id of the newest attachment with this name, asked of the PROJECT.
 *
 * This is the whole difference between an upsert and an append, and it is what
 * `~/Documents/code/paper` does that gTables did not. There the target id comes
 * from a live mirror of the project's attachment list, refreshed after every
 * upload; here the id lived in a module-level Map that only ever filled on a
 * successful READ. The read was failing, so every save had no id, and every
 * save therefore created a new row — which is how one project ended up with
 * dozens of `gtables-workbook.json`.
 *
 * The session cache is now only a fallback for when the project cannot be
 * asked, not the source of truth.
 */
async function findAttachmentId(name: string): Promise<number | null> {
  const details = await fetchProjectDetails();
  if (!details) return attachmentIdByName.get(name) ?? null;
  const rows = extractAttachments(details).filter((a) => a?.name === name && typeof a.id === 'number');
  if (rows.length === 0) {
    attachmentIdByName.delete(name);
    return null;
  }
  rows.sort((a, b) => String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? '')));
  const id = rows[0]!.id!;
  attachmentIdByName.set(name, id);
  return id;
}

/**
 * Upload a JSON-serializable object as a project attachment. Upsert, never add.
 *
 * There must only ever be ONE row per name. So the target id is asked of the
 * project before every write, the project is asked again after a create — the
 * host's response does not always carry the new id, and its shape varies across
 * builds — and a create is allowed at most once per session per name. That last
 * guard exists because the failure being fixed was a create that could not
 * learn its own id and so repeated on every save.
 */
export async function uploadJsonAttachment(
  name: string,
  obj: unknown,
): Promise<{ attachmentId: number } | null> {
  const buffer = new TextEncoder().encode(JSON.stringify(obj));

  const doUpload = async (attachmentId: number | null): Promise<number | null> => {
    const payload: Record<string, unknown> = { buffer, contentType: 'application/json', name };
    if (attachmentId != null) payload.attachmentId = attachmentId;
    const res = await rpc.invoke('uploadProjectAttachment', [payload]);
    return pickAttachmentId(res) ?? attachmentId;
  };

  const id = await findAttachmentId(name);

  if (id == null && created.has(name)) {
    // One was made earlier and the project still does not list it. Making
    // another is exactly how the pile grew; fail instead and let the caller say
    // so, which is recoverable, where a second row is not.
    console.warn('[attachmentStore] refusing a second create for', name);
    return null;
  }

  try {
    const newId = await doUpload(id);
    if (id == null) created.add(name);
    // Ask the project again either way: the id it reports is the one the next
    // save has to target, and a response that carried one may still be wrong
    // about which row now holds the bytes.
    const settled = (await findAttachmentId(name)) ?? (typeof newId === 'number' ? newId : null);
    if (settled == null) return null;
    attachmentIdByName.set(name, settled);
    return { attachmentId: settled };
  } catch (e) {
    /*
     * The id may be dead — the row was deleted in the Giraffe UI. That is the
     * one case where a create is right, and it is allowed once.
     */
    if (id != null && !created.has(name)) {
      attachmentIdByName.delete(name);
      try {
        const retryId = await doUpload(null);
        created.add(name);
        const settled = (await findAttachmentId(name)) ?? (typeof retryId === 'number' ? retryId : null);
        return settled == null ? null : { attachmentId: settled };
      } catch (e2) {
        console.warn('[attachmentStore] upload retry failed', e2);
        return null;
      }
    }
    console.warn('[attachmentStore] upload failed', e);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────

function extractAttachments(details: unknown): AttachmentRow[] {
  if (!details || typeof details !== 'object') return [];
  const o = details as Record<string, unknown>;
  const candidates: unknown[] = [
    o['attachments'],
    (o['data'] as Record<string, unknown> | undefined)?.['attachments'],
    (o['project'] as Record<string, unknown> | undefined)?.['attachments'],
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c as AttachmentRow[];
  }
  return [];
}

/** The app is served from the host's own origin, so its API needs no CORS. */
function sameOriginAsParent(): boolean {
  const origin = parentOrigin();
  return origin !== '' && origin === window.location.origin;
}

/**
 * Fetch project details.
 *
 * Through the RPC, so the HOST makes the request from its own origin. A direct
 * hit is faster and was tried first, and from an app subdomain it can never
 * work: `/v6/api/project-details/<pid>` answers with no
 * `Access-Control-Allow-Origin` at all, which the browser refuses before the
 * body arrives — a guaranteed console error and a wasted round trip on every
 * load. It is still tried when the app is served from the host origin, where
 * there is no CORS to fail and it is genuinely quicker.
 *
 * The RPC's own failure mode is why each result is validated rather than
 * trusted: on a cold start it can answer with an HTML error page, and a
 * truthy string would otherwise sail through as "details".
 */
async function fetchProjectDetails(): Promise<unknown | null> {
  const pid = projectId();
  const origin = parentOrigin();

  const attemptOnce = async (): Promise<unknown | null> => {
    const tries: Array<() => Promise<unknown | null>> = [
      () => rpc.invoke('fetchProjectDetails', pid != null ? [String(pid)] : []),
      () => rpc.invoke('fetchProjectDetails', pid != null ? [pid] : []),
      () => rpc.invoke('fetchProjectDetails', []),
    ];
    if (sameOriginAsParent() && origin && pid != null) {
      tries.push(async () => {
        const resp = await fetch(`${origin}/v6/api/project-details/${pid}`, {
          credentials: 'include',
        });
        if (!resp.ok) return null;
        if (!(resp.headers.get('content-type') || '').includes('json')) return null;
        return resp.json();
      });
    }
    for (const fn of tries) {
      try {
        const r = await fn();
        // An object, not an HTML error page that happens to be truthy.
        if (r && typeof r === 'object') return r;
      } catch {
        /* try the next shape */
      }
    }
    return null;
  };

  for (const backoff of [0, 500, 1000]) {
    if (backoff) await delay(backoff);
    const details = await attemptOnce();
    if (details) return details;
  }
  // Worth saying out loud: this is the read whose failure stops the app saving
  // to the project at all, and on a cold start it usually means the bridge was
  // not warm yet rather than anything being wrong.
  console.warn('[attachmentStore] fetchProjectDetails found nothing after 3 attempts; projectId =', pid);
  return null;
}

/**
 * Resolve a raw attachment url through `getTempUrl`, then fetch it. Retries
 * on the stale-url / upload-race statuses (re-minting a fresh signed url each
 * time) with a short backoff. Returns the response text, or null.
 */
async function fetchAttachmentText(rawUrl: string): Promise<string | null> {
  const absUrl = absoluteUrl(rawUrl);

  /*
   * NO `credentials`. That one word was the bug.
   *
   * A signed googleapis url needs no cookie — the signature IS the credential —
   * and asking for one changes the CORS contract underneath: a credentialed
   * request refuses a wildcard `Access-Control-Allow-Origin` outright and
   * demands `Access-Control-Allow-Credentials: true` beside it, which GCS does
   * not send. The browser then rejects a response it would otherwise have
   * accepted, and the console blames the origin rather than the request.
   * (`~/Documents/code/paper` fetches these bare and has always worked.)
   */
  const mintAndFetch = async (): Promise<Response | null> => {
    const res = await rpc.invoke('getTempUrl', [absUrl]);
    const signed =
      typeof res === 'string'
        ? res
        : ((res as Record<string, unknown> | null)?.['url'] as string) ??
          ((res as Record<string, unknown> | null)?.['tempUrl'] as string) ??
          ((res as Record<string, unknown> | null)?.['signedUrl'] as string) ??
          '';
    // Empty means the storage layer was never reached, which is a different
    // thing from a response that arrived and was not OK.
    if (!signed) return null;
    /*
     * `cache: 'no-store'`, because the HOST has already fetched this object.
     *
     * Same url, same browser cache — and the cached entry carries the CORS
     * headers from the host's request, `Access-Control-Allow-Origin:
     * https://staging.giraffe.build`. Ours revalidates, gets a 304, and the
     * browser reuses those headers, so the check runs against an origin that
     * was never ours: "has a value 'https://staging.giraffe.build' that is not
     * equal to the supplied origin", on a 304. GCS never sent us that header;
     * we were shown someone else's.
     *
     * A cache-busting query parameter is not available here — it would void the
     * signature — so the request has to decline the cache outright.
     */
    return fetch(signed, { cache: 'no-store' });
  };

  for (const backoff of [0, 750, 1500]) {
    if (backoff) await delay(backoff);
    try {
      const resp = await mintAndFetch();
      if (!resp) {
        console.warn('[attachmentStore] getTempUrl returned no url for', absUrl);
        continue;
      }
      if (resp.ok) return resp.text();
      if (!RETRYABLE_STATUS.includes(resp.status)) {
        console.warn('[attachmentStore] attachment fetch failed hard:', resp.status, resp.statusText);
        return null;
      }
      console.warn('[attachmentStore] attachment fetch', resp.status, '— re-minting the url');
    } catch (e) {
      // A CORS refusal lands here with nothing useful in it, which is why the
      // two rules in mintAndFetch above (no credentials, no cache) are written
      // out at length: this catch cannot tell you which one you broke.
      console.warn('[attachmentStore] attachment fetch threw', e);
    }
  }
  return null;
}

/**
 * Load + parse a JSON attachment by `name`. Returns the parsed value (typed
 * as T — no schema validation here; the caller validates), or null when the
 * attachment is missing/unreachable/corrupt.
 *
 * Picks the session-cached id first, else the newest row by `updated_at`.
 */
/**
 * What a read found — and, when it found nothing, WHY.
 *
 * `missing` and `unreadable` are the same absence to a caller that only has
 * null to work with, and they could not be more different: one means this
 * project has no workbook yet and it is safe to write one, the other means the
 * project HAS one that this origin could not read, and writing over it destroys
 * someone's model. That distinction is the whole reason this type exists.
 */
export type AttachmentRead<T> =
  | { kind: 'ok'; value: T }
  | { kind: 'missing' }
  | { kind: 'unreadable'; detail: string };

export async function readJsonAttachment<T>(name: string): Promise<AttachmentRead<T>> {
  const details = await fetchProjectDetails();
  if (!details) {
    return { kind: 'unreadable', detail: 'Could not read this project’s details.' };
  }

  const rows = extractAttachments(details).filter((a) => a?.name === name && a?.url);
  if (!rows.length) return { kind: 'missing' };

  // Prefer the id we last wrote; else newest by updated_at.
  const cachedId = attachmentIdByName.get(name) ?? null;
  let chosen = cachedId != null ? rows.find((a) => a.id === cachedId) : undefined;
  if (!chosen) {
    rows.sort((a, b) =>
      String(b.updated_at || '').localeCompare(String(a.updated_at || '')),
    );
    chosen = rows[0];
  }
  if (chosen && typeof chosen.id === 'number') attachmentIdByName.set(name, chosen.id);

  const text = chosen?.url ? await fetchAttachmentText(chosen.url) : null;
  if (text == null) {
    return { kind: 'unreadable', detail: 'The project has a saved workbook, but it could not be fetched.' };
  }
  try {
    return { kind: 'ok', value: JSON.parse(text) as T };
  } catch {
    // A corrupt blob is not a missing one either: starting from defaults and
    // saving over it is how the corruption becomes permanent.
    return { kind: 'unreadable', detail: 'The saved workbook could not be read as JSON.' };
  }
}

/** The old shape, for callers that genuinely only care whether they got it. */
export async function loadJsonAttachment<T>(name: string): Promise<T | null> {
  const read = await readJsonAttachment<T>(name);
  return read.kind === 'ok' ? read.value : null;
}
